import React, { useState } from 'react';
import { useController } from 'react-hook-form';
import { toast } from 'sonner';
import { useLoadingState } from '../hooks/useLoader';
import DeleteUploadedDataIcon from '../assets/icons/DeleteUploadedDataIcon';
import { uploader } from '../helpers/uploader';
import { compressPdf } from '../helpers/pdfCompressor';
import Loader from './Loader';

const InputForUploadingPDFWithLimit = ({
    name,
    control,
    errors,
    error,
    placeholder = 'criteria',
    width = 'w-full',
    type = 'file',
    uploadError,
    setUploadError,
    isUploaded,
    validateObject = {},
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [compressionStatus, setCompressionStatus] = useState('idle'); // idle, compressing, error
    const [compressionProgress, setCompressionProgress] = useState('');

    const { loading, startLoading, stopLoading } = useLoadingState();
    const { field } = useController({
        name,
        control,
        rules: validateObject,
    });

    const handleFileChange = async (event) => {
        let file = event.target.files[0];
        const maxFileSize = 9.5 * 1024 * 1024; // 9.5MB

        setUploadError('');
        setCompressionStatus('idle');

        if (!file) return;

        console.log('File selected:', file.name, 'Size:', file.size, 'Type:', file.type);

        if (file.type !== 'application/pdf') {
            setUploadError('Please select a valid PDF file.');
            return;
        }

        try {
            if (file.size > maxFileSize) {
                console.log('File > 9.5MB. Starting compression loop...');
                setCompressionStatus('compressing');

                const MAX_PASSES = 4;
                let currentPass = 1;
                let compressedFile = file;

                while (currentPass <= MAX_PASSES && compressedFile.size > maxFileSize) {
                    setCompressionProgress(`Compressing...`);
                    // Simplified text as requested
                    console.log(`Pass ${currentPass}: Starting compression (extreme)...`);

                    try {
                        const newFile = await compressPdf(compressedFile, 'extreme');
                        console.log(`Pass ${currentPass} result:`, newFile.size);

                        // If compression didn't help (or increased size), stop to prevent infinite futility
                        if (newFile.size >= compressedFile.size) {
                            console.warn(`Pass ${currentPass} did not reduce file size. Stopping.`);
                            break;
                        }

                        compressedFile = newFile;

                        if (compressedFile.size <= maxFileSize) {
                            console.log('Target size reached!');
                            break;
                        }

                        currentPass++;
                    } catch (err) {
                        console.error(`Pass ${currentPass} failed:`, err);
                        setCompressionStatus('error');
                        setUploadError('Compression failed. Please check your connection and try again.');
                        return;
                    }
                }

                file = compressedFile;

                if (file.size > maxFileSize) {
                    console.error('Final file still too large.');
                    setCompressionStatus('error');
                    setUploadError(`File is too large to compress under 9.5MB (Best: ${(file.size / (1024 * 1024)).toFixed(2)}MB). Please use a smaller file.`);
                    return;
                }

                // Compression successful, proceed to upload
                setCompressionStatus('idle');
                toast.success('Compression successful');
            } else {
                console.log('File size within limits.');
            }

            setSelectedFile(file);
            await uploadFile(file);

        } catch (error) {
            console.error('Error in handleFileChange:', error);
            setCompressionStatus('error');
            setUploadError('An error occurred during file processing.');
        }
    };

    const uploadFile = async (file) => {
        try {
            console.log('Uploading to backend...');
            startLoading();
            const res = await uploader(file, 'doc');
            if (res.files && res.files.length) {
                field.onChange(res.files[0]);
                toast.success('File is successfully uploaded');
            } else {
                toast.error('Sorry, unable to upload the file');
            }
            stopLoading();
        } catch (error) {
            console.error('Backend upload error:', error);
            setUploadError('File upload failed.');
            stopLoading();
        }
    };

    return (
        <>
            {isUploaded ? (
                <button
                    onClick={() => field.onChange('')}
                    type="button"
                    className="bg-grayBgNormal w-32 flex items-center border border-dashed rounded-md border-borderUnderline shadow-sm px-4 py-2"
                >
                    <span className="truncate w">{isUploaded}</span>
                    <span>
                        <DeleteUploadedDataIcon />
                    </span>
                </button>
            ) : (
                <>
                    {compressionStatus === 'compressing' ? (
                        <div className="bg-grayBgNormal border border-dashed rounded-md border-borderUnderline shadow-sm px-4 py-2 flex items-center justify-center gap-2">
                            <Loader w="w-4" h="h-4" fillColor="fill-blue-600" />
                            <p className="text-sm text-textPrimary font-medium animate-pulse">Compressing...</p>
                        </div>
                    ) : (
                        <>
                            <label className="flex flex-col gap-2 items-center" htmlFor="docFile">
                                <p className="bg-grayBgNormal border cursor-pointer border-dashed rounded-md border-borderUnderline shadow-sm px-4 py-2">
                                    Click to Upload
                                </p>
                            </label>
                            <input
                                {...field}
                                value={field.value}
                                onChange={handleFileChange}
                                type="file"
                                id="docFile"
                                name="docFile"
                                accept="application/pdf"
                                className="hidden"
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default InputForUploadingPDFWithLimit;
